import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/Monitoring';
import { LabelProps } from 'recharts';

import { moment } from '../lib/moment';
import { BarChartType, Timeseries } from '../model/solomon';

const t = i18n(keyset);

const dayRangeFormat = 'HH:mm';
const monthRangeFormat = 'DD MMM';
const monthRangeFormatWithTime = 'DD MMM HH:mm:ss';

export const tickFormatter = (value: string, format: string) => moment(value).format(format);
export const barChartTickFFormatter = (value: string, format: string) =>
    moment(value.split('-')[0], monthRangeFormatWithTime).format(format);
export const labelFormatter = (value: string | number) => moment(value).format('DD MMM YYYY HH:mm:ss');

export const getTickFormat = (fromTs: number, toTs: number) => {
    const from = moment(fromTs);
    const to = moment(toTs);

    const delta = to.valueOf() - from.valueOf();
    const DAY = 1000 * 60 * 60 * 24;

    if (delta <= DAY) {
        return dayRangeFormat;
    }

    return monthRangeFormat;
};

export const getTicks = (fromTs: number, toTs: number, timestamps: number[]) => {
    const from = moment(fromTs);
    const to = moment(toTs);

    const delta = to.valueOf() - from.valueOf();
    const DAY = 1000 * 60 * 60 * 24;

    const avgDays = Math.ceil((from.daysInMonth() + to.daysInMonth()) / 2);
    let gap = Math.floor(timestamps.length / avgDays);

    if (delta <= DAY * 7) {
        gap = Math.floor(timestamps.length / 7);
    }

    if (delta <= DAY) {
        gap = Math.floor(timestamps.length / 24);
    }

    if (delta <= 1000 * 60 * 30) {
        gap = Math.floor(timestamps.length / to.diff(from, 'minutes'));
    }

    return timestamps.filter((_, i) => i % gap === 0);
};

type ChartState = 'loading' | 'error';

export const getChartStateLabel = (state?: ChartState) => {
    const fontSize = 13;

    switch (state) {
        case 'error':
            return { value: t('chart-error'), fill: '#cb2d2d', fontSize, position: 'center' } as const;
        case 'loading':
            return { value: t('chart-loading'), fontSize, position: 'center' } as const;
        default:
            return undefined;
    }
};

export const getMonitoringStubData = (from: number, to: number) => {
    const delta = to - from;
    const points = 500; // При смене тут, нужно также поменять maxPoints в api

    return new Array(points)
        .fill(null)
        .map((_, i) => ({
            x: from + Math.floor((delta * i) / points),
            y: i / points,
        }))
        .concat([{ x: to, y: 1 }]);
};

export const timeseriesToData = (timeseriesArr: Timeseries[] | undefined) => {
    if (
        !timeseriesArr ||
        timeseriesArr.length === 0 ||
        timeseriesArr.every((timeseries) => timeseries.timestamps.length === 0)
    ) {
        return undefined;
    }
    const labels = timeseriesArr.map(({ label }) => label);

    const timestampsesArr = timeseriesArr.map((x) => x.timestamps);
    const timestamps = timestampsesArr.sort((x, y) => y.length - x.length)[0];

    const data = timestamps.map((ts, i) => {
        const obj: {
            ts: number;
            [key: string]: number | null;
        } = { ts };

        timeseriesArr.forEach((timeseries) => {
            obj[timeseries.label] = timeseries.values[i];
        });

        return obj;
    });

    return { data, labels };
};

export const barsToData = (bars: BarChartType['data']) => {
    if (!bars || bars.length === 0) {
        return undefined;
    }

    const { from, to, ...rest } = bars[0];
    const labels = Object.keys(rest);

    const data = bars.map((bar) => ({
        ...bar,
        label: `${moment(bar.from).format(monthRangeFormatWithTime)} - ${moment(bar.to).format(
            monthRangeFormatWithTime,
        )}`,
    }));

    return { data, labels };
};

export const getDayRange = () => {
    const now = Date.now();

    return {
        from: now - 1000 * 60 * 60 * 24,
        to: now,
    };
};

export const getAliceAppYAxisLabelProps = (value: string): LabelProps => ({
    value,
    position: 'insideLeft',
    fill: 'rgb(71, 81, 94)', // --g-color-private-cool-grey-800-solid
    angle: -90,
    style: { textAnchor: 'middle' },
    fontSize: 13,
});
